import React, { useCallback, useMemo, useState } from 'react';
import { Popover, Text, TextList, TextListItem } from '@oneaudi/unified-web-components';
import { useTranslation } from 'react-i18next';
import {
  CheckboxWrapper,
  Label,
  Checkbox,
  HiddenInput,
  Hint,
  InfoIconWrapper,
  ButtonStyled,
  Content,
  DescriptionText,
  Description,
} from './CheckboxSelector.styles';
import CheckMarkIcon from '../../assets/icons/CheckMarkIcon';
import listOfDealers from '../../constants/ListOfDealers';

interface CheckboxTypes {
  value: string;
  checked: boolean;
  onChange: (option: string, newChecked: boolean) => void;
  label: string | React.ReactNode;
  ariaLabel: string;
  inputName: string;
  disabled?: boolean;
  hint?: string;
  filterType?: string;
}

const CheckboxSelector: React.FC<CheckboxTypes> = ({
  value,
  checked,
  onChange,
  label,
  ariaLabel,
  inputName,
  disabled = false,
  hint,
  filterType,
}) => {
  const { t, i18n } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const isRegionFilter = filterType === 'offerRegion';
  const handleChange = useCallback(() => {
    if (disabled) return;
    onChange(value, !checked);
  }, [disabled, onChange, value, checked]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key !== ' ') return;
      e.preventDefault();
      handleChange();
    },
    [disabled, handleChange],
  );

  const InfoPopover = useMemo(() => {
    if (!isRegionFilter) return null;
    return (
      <InfoIconWrapper>
        <Popover
          aria-label="Popover title"
          id="popover_title"
          triggerElement={
            <ButtonStyled
              variant="tertiary"
              icon="info-circle"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
            />
          }
          open={isOpen}
          content={
            <>
              <DescriptionText>
                <Text
                  variant={{
                    order: '2',
                    style: 'normal',
                  }}
                >
                  {t(`promo.filters.filterOptions.offerRegion.listOfDealers`)}
                </Text>
              </DescriptionText>
              <TextList
                size="sm"
                className="dealers-list"
                data-testid="dealers-list"
                variant="bullet"
              >
                {listOfDealers[value].map((dealer) => (
                  <TextListItem key={dealer}>{dealer}</TextListItem>
                ))}
              </TextList>
            </>
          }
          onClose={() => setIsOpen(false)}
          withTip
        />
      </InfoIconWrapper>
    );
  }, [isOpen, isRegionFilter, t, value]);

  return (
    <CheckboxWrapper disabled={disabled}>
      <Checkbox
        tabIndex={disabled ? -1 : 0}
        onClick={handleChange}
        role="checkbox"
        aria-label={ariaLabel}
        aria-checked={checked}
        onKeyDown={handleKeyPress}
        checked={checked}
        disabled={disabled}
      >
        {checked && <CheckMarkIcon />}
      </Checkbox>
      <HiddenInput
        id={inputName}
        type="checkbox"
        onChange={handleChange}
        checked={checked}
        name={value}
        disabled={disabled}
      />
      <Label data-testid={`${filterType}-${inputName}`} htmlFor={inputName} disabled={disabled}>
        {label}
        {!!hint && <Hint disabled={disabled}> ({hint}) </Hint>}
      </Label>
      {InfoPopover}
    </CheckboxWrapper>
  );
};

export default CheckboxSelector;
